/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { authState } from '../../store/auth';
import { userState } from '../../store/user';

declare global {
  interface Window {
    axw: any;
  }
}

/**
 * Identity bridge for the Axon Webchat widget.
 *
 * The widget itself is loaded by the embed snippet in public/index.html — this
 * component only tells it who the visitor is. Commands are safe to fire at any
 * time: before the loader finishes downloading they are queued by the shim that
 * the snippet defines ahead of the script tag.
 *
 * The `user_hash` is an HMAC the backend computes over
 * `id \n email \n phoneNumber \n ""` and returns on the auth response — we only
 * pass through the exact same raw strings it signed, never recompute anything
 * here (the verify secret must never reach the browser).
 */
const AxonWebchat = () => {
  const { token } = useRecoilValue(authState);
  const user = useRecoilValue(userState);
  const booted = useRef(false);

  useEffect(() => {
    if (typeof window.axw !== 'function') return;

    if (token && user?.id && user?.userHash) {
      window.axw('boot', {
        user_id: user.id,
        user_hash: user.userHash,
        email: user.email,
        phone: user.phoneNumber,
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      });
      booted.current = true;
    } else if (booted.current) {
      // Clears the visitor's session from the widget's own storage. This is
      // client-side only — there is no server-side session to revoke.
      window.axw('shutdown');
      booted.current = false;
    }
  }, [token, user?.id, user?.userHash]);

  return null;
};

export default AxonWebchat;
